import { addDuration } from "../../utils/date-helper";
import { getTermStartDate } from "../../utils/term-helper";
import { TTLCache } from "../../utils/ttl-cache";
export class SACBaseService {
    baseUrl;
    fetchImpl;
    encoder = (input) => btoa(input);
    serializer;
    deserializer;
    classListCache = new TTLCache(3600000);
    lecturesCache = new TTLCache(900000);
    tokenExpiredHandler;
    onTokenExpired(handler) {
        this.tokenExpiredHandler = handler;
    }
    constructor(baseUrl, fetchImpl, serializer, deserializer) {
        this.baseUrl = baseUrl;
        this.fetchImpl = fetchImpl;
        this.serializer = serializer;
        this.deserializer = deserializer;
    }
    async request(path, options) {
        if (!this.baseUrl)
            throw new Error("baseUrl is not defined");
        const url = `${this.baseUrl}${path}`;
        const response = await this.fetchImpl(url, {
            ...options,
            body: JSON.stringify(options.body),
        });
        const data = await response.json();
        if (!response.ok)
            throw new Error(data?.message || `HTTP error! Status: ${response.status}`);
        return data;
    }
    async requestWithAuth(path, options) {
        const deserializedData = this.deserializer();
        if (!deserializedData ||
            !deserializedData.firstName ||
            !deserializedData.lastName ||
            !deserializedData.token) {
            throw new Error("Unauthenticated data");
        }
        const { token } = deserializedData;
        options.headers = {
            ...options.headers,
            Authorization: token,
        };
        const data = await this.request(path, options);
        if (data.status === "0" && data.message?.toLowerCase().includes("token expired")) {
            this.logout();
            this.tokenExpiredHandler?.();
            throw new Error(data.message);
        }
        return data;
    }
    teacherInfo() {
        const deserializedData = this.deserializer();
        return {
            firstName: deserializedData.firstName,
            lastName: deserializedData.lastName,
            teacherId: deserializedData.teacherId,
        };
    }
    isAuthenticated() {
        const deserializedData = this.deserializer();
        return !!(deserializedData &&
            deserializedData.firstName &&
            deserializedData.lastName &&
            deserializedData.token &&
            deserializedData.teacherId);
    }
    async checkAuth() {
        try {
            const { teacherId } = this.teacherInfo();
            // Use the same window as the lecture sync (start of current term to
            // +12 months) so this validation call is cheap and shares the
            // lectures cache with syncFromSAC callers.
            await this.getLecturesByTeacherId(teacherId, getTermStartDate(new Date()), addDuration(new Date(), 12, "month"));
            return true;
        }
        catch (err) {
            console.error(err);
            // clear the current session
            this.serializer({
                firstName: undefined,
                lastName: undefined,
                teacherId: undefined,
                token: undefined,
            });
            return false;
        }
    }
    logout() {
        this.serializer({
            token: undefined,
            firstName: undefined,
            lastName: undefined,
            teacherId: undefined,
        });
    }
    async login(username, password) {
        if (!this.serializer)
            throw new Error("serializer not available");
        const body = {
            operation: "loginAuth",
            encodeUsername: this.encoder ? this.encoder(username) : username,
            encodePassword: this.encoder ? this.encoder(password) : password,
        };
        const data = await this.request("", {
            method: "POST",
            cors: true,
            headers: {
                "Content-Type": "application/json",
            },
            body: body,
        });
        if (data.status === "1" && data.coachToken && data.coachToken !== "NULL") {
            const result = {
                token: data.coachToken,
                firstName: data.first_name,
                lastName: data.last_name,
                teacherId: data.coach_id,
            };
            this.serializer(result);
            return result;
        }
        else {
            throw new Error(data.message ?? "Login error");
        }
    }
    async loginWithTempToken(tempToken, coachId) {
        if (!this.serializer)
            throw new Error("serializer not available");
        const body = {
            operation: "loginAuthWithTempToken",
            tempToken: tempToken,
            coach_id: coachId,
        };
        const data = await this.request("", {
            method: "POST",
            cors: true,
            headers: {
                "Content-Type": "application/json",
            },
            body: body,
        });
        if (data.status === "1" && data.coachToken && data.coachToken !== "NULL") {
            const result = {
                token: data.coachToken,
                firstName: data.first_name,
                lastName: data.last_name,
                teacherId: data.coach_id,
            };
            this.serializer(result);
            return result;
        }
        else {
            throw new Error(data.message ?? "Login error");
        }
    }
    async getClassList(year, term) {
        const cacheKey = `${year}_${term}_${this.teacherInfo().teacherId}`;
        const cached = this.classListCache.get(cacheKey);
        if (cached !== undefined)
            return cached;
        const body = {
            operation: "loadClassList",
            year: year,
            term: term,
            coach_id: this.teacherInfo().teacherId,
        };
        const data = await this.requestWithAuth("", {
            method: "POST",
            cors: false,
            headers: {
                "Content-Type": "application/json",
            },
            body: body,
        });
        if (data.status === "1") {
            const result = data.classArray?.map((cls) => ({
                groupName: cls.group_name,
                schoolName: cls.school_name,
                schoolId: cls.school_id,
                classId: cls.cid,
            })) ?? [];
            this.classListCache.set(cacheKey, result);
            return result;
        }
        else if (data.status === "0") {
            // Coach has no classes for this year/term - return empty array instead of throwing error
            return [];
        }
        else {
            throw new Error("Failed to load class list");
        }
    }
    async getClassDetails(year, term, classId, classDateId) {
        const body = {
            operation: "loadStudentList",
            year: year,
            term: term,
            class_id: classId,
            class_date_id: classDateId,
            coach_id: this.teacherInfo().teacherId,
        };
        const data = await this.requestWithAuth("", {
            method: "POST",
            cors: true,
            headers: {
                "Content-Type": "application/json",
            },
            body: body,
        });
        if (data.status === "1") {
            return {
                topic: data.topic,
                schoolName: data.schoolName,
                classStartDate: data.classStartDate,
                students: data.studentArray?.map((student) => {
                    // Extract JCL rating from 'jcl' field if present and >= 0
                    let jclRating = null;
                    if (typeof student.jcl !== "undefined") {
                        const val = Number(student.jcl);
                        if (!isNaN(val) && val >= 0)
                            jclRating = val;
                    }
                    return {
                        studentId: student.student_id,
                        firstName: student.first_name,
                        lastName: student.last_name,
                        nickname: student.nickname || null,
                        className: student.class_name || null,
                        score: Number(student.score),
                        attend: student.attend === "1",
                        behavior: Number(student.behavior) + 3,
                        nthPaymentRemainder: Number(student.nthPaymentRemainder),
                        termPaidStatus: student.termPaidStatus,
                        parentContactIsEmpty: student.parentContactIsEmpty,
                        awards: student.awards
                            ? student.awards
                                .split(",")
                                .map((code) => Number(code))
                                .filter((code) => !isNaN(code))
                            : [],
                        chessSquadUsername: student.chessSquadUsername,
                        jclRating,
                    };
                }) ?? [],
            };
        }
        else {
            throw new Error("Failed to load class details");
        }
    }
    async updateStudentList(year, term, classDateId, classId, topic, students) {
        const studentList = students.map((s) => ({
            studentid: s.studentId,
            score: s.score.toString(),
            attend: s.attend ? "1" : "0",
            behavior: (s.behavior - 3).toString(),
            awards: s.awards ? s.awards.join(",") : "",
        }));
        const body = {
            operation: "updateStudentList",
            year: year,
            term: term,
            class_id: classId,
            class_date_id: classDateId,
            topic,
            coach_id: this.teacherInfo().teacherId,
            studentlist: studentList,
        };
        const data = await this.requestWithAuth("", {
            method: "POST",
            cors: true,
            headers: {
                "Content-Type": "application/json",
            },
            body: body,
        });
        if (data.status !== "1") {
            throw new Error("Failed to update student list");
        }
    }
    async fetchStudentsToAdd(year, term, classId, classDateId) {
        const body = {
            operation: "fetchStudentsToAdd",
            year: year,
            term: term,
            class_id: classId,
            class_date_id: classDateId,
            coach_id: this.teacherInfo().teacherId,
        };
        const data = await this.requestWithAuth("", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: body,
        });
        if (data.status === "1") {
            const mapStudent = (s) => ({
                studentId: String(s.sid ?? s.student_id ?? ""),
                firstName: s.first_name,
                lastName: s.last_name,
                nickname: s.nickname || null,
                className: s.class_name || null,
                // Only prior-year candidates carry a `year`; current-year ones don't.
                year: s.year || null,
            });
            // Combine prior-year candidates with students already enrolled earlier in
            // the current year (the two cases the duplicate-check covers), de-duped.
            const seen = new Set();
            const result = [];
            for (const s of [
                ...(data.previousYears ?? []),
                ...(data.currentYearExisting ?? []),
            ]) {
                const mapped = mapStudent(s);
                if (!mapped.studentId || seen.has(mapped.studentId))
                    continue;
                seen.add(mapped.studentId);
                result.push(mapped);
            }
            return result;
        }
        else if (data.status === "0") {
            // No candidate students found - return empty array instead of throwing
            return [];
        }
        else {
            throw new Error(data.message ?? "Failed to fetch students to add");
        }
    }
    async addExistingStudent(year, term, classId, classDateId, studentId) {
        const body = {
            operation: "addExistingStudent",
            year: year,
            term: term,
            class_id: classId,
            class_date_id: classDateId,
            studentid: studentId,
            coach_id: this.teacherInfo().teacherId,
        };
        const data = await this.requestWithAuth("", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: body,
        });
        if (data.status !== "1") {
            throw new Error(data.message ?? "Failed to add student to roll");
        }
    }
    async addNewStudent(year, term, classId, classDateId, student) {
        const body = {
            operation: "addNewStudent",
            year: year,
            term: term,
            class_id: classId,
            class_date_id: classDateId,
            firstname: student.firstName,
            lastname: student.lastName,
            nickname: student.nickname ?? "",
            classname: student.className ?? "",
            coach_id: this.teacherInfo().teacherId,
        };
        const data = await this.requestWithAuth("", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: body,
        });
        if (data.status !== "1") {
            throw new Error(data.message ?? "Failed to add new student");
        }
    }
    async getLecturesByTeacherId(teacherId, fromDate, toDate) {
        const dayOf = (d) => Math.floor(d.getTime() / 86400000);
        const cacheKey = `${teacherId}_${dayOf(fromDate)}_${dayOf(toDate)}`;
        const cached = this.lecturesCache.get(cacheKey);
        if (cached !== undefined)
            return cached;
        const body = {
            operation: "returnListOfClassDatesByCoachIdandTimeFrame",
            coach_id: teacherId,
            fromDate: Math.floor(fromDate.getTime() / 1000),
            toDate: Math.floor(toDate.getTime() / 1000),
        };
        const data = await this.requestWithAuth("", {
            method: "POST",
            cors: true,
            headers: {
                "Content-Type": "application/json",
            },
            body: body,
        });
        if (data.status === "1") {
            const result = data.result.map((r) => {
                return {
                    cdid: r.cdid,
                    classId: r.class_id,
                    year: r.year,
                    term: r.term,
                    day: r.day,
                    date: r.date,
                    startTime: r.start_time,
                    endTime: r.end_time,
                    coachId: r.coach_id,
                    coachReminded: r.coach_reminded,
                    rollSubmitted: r.roll_submitted,
                    rollApproved: r.roll_approved,
                    rollApprovedBy: r.roll_approved_by,
                    topicsCovered: r.topics_covered,
                    firstName: r.first_name,
                    lastName: r.last_name,
                };
            });
            this.lecturesCache.set(cacheKey, result);
            return result;
        }
        else if (data.status === "0" && !data.message) {
            // SAC returns a bare {status: "0"} (no message) when the coach simply
            // has no lectures in the window. A status "0" WITH a message is an
            // error (e.g. "Ip Address is not allowed") — treating it as an empty
            // list would make syncFromSAC delete the coach's lectures, so throw.
            return [];
        }
        else {
            throw new Error(data.message || "Failed to load lectures by coach");
        }
    }
    async getLecturesByClassId(classId) {
        const body = {
            operation: "returnListOfClassDatesByClassId",
            class_id: classId,
            coach_id: this.teacherInfo().teacherId,
        };
        const data = await this.requestWithAuth("", {
            method: "POST",
            cors: true,
            headers: {
                "Content-Type": "application/json",
            },
            body: body,
        });
        if (data.status === "1") {
            return data.result.map((r) => {
                return {
                    cdid: r.cdid,
                    classId: r.class_id,
                    year: r.year,
                    term: r.term,
                    day: r.day,
                    date: r.date,
                    startTime: r.start_time,
                    endTime: r.end_time,
                    coachId: r.coach_id,
                    coachReminded: r.coach_reminded,
                    rollSubmitted: r.roll_submitted,
                    rollApproved: r.roll_approved,
                    rollApprovedBy: r.roll_approved_by,
                    topicsCovered: r.topics_covered,
                    firstName: r.first_name,
                    lastName: r.last_name,
                };
            });
        }
        else if (data.status === "0" && !data.message) {
            // Class has no lectures - return empty array instead of throwing error
            return [];
        }
        else {
            throw new Error(data.message || "Failed to load lectures by class id");
        }
    }
}
